import { ThemeEvents, CartUpdatedEvent } from '@theme/events';

/* ---------- Date de livraison estimée (jours ouvrés, fériés français, coupure 14 h, heure de Paris) ---------- */
const DAY = 864e5;
const JOURS = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
const MOIS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const holidayCache = {};

function easter(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return Date.UTC(y, month - 1, day);
}
function holidays(y) {
  if (!holidayCache[y]) {
    const fixed = [[0, 1], [4, 1], [4, 8], [6, 14], [7, 15], [10, 1], [10, 11], [11, 25]].map(([m, d]) => Date.UTC(y, m, d));
    const e = easter(y);
    holidayCache[y] = new Set([...fixed, e + DAY, e + 39 * DAY, e + 50 * DAY]);
  }
  return holidayCache[y];
}
function isWorkday(t) {
  const d = new Date(t), w = d.getUTCDay();
  return w !== 0 && w !== 6 && !holidays(d.getUTCFullYear()).has(t);
}
function addWorkdays(t, n) {
  while (n > 0) { t += DAY; if (isWorkday(t)) n--; }
  return t;
}
function parisNow() {
  const parts = {};
  new Intl.DateTimeFormat('en-GB', { timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', hourCycle: 'h23' })
    .formatToParts(new Date()).forEach((p) => (parts[p.type] = p.value));
  return { t: Date.UTC(+parts.year, +parts.month - 1, +parts.day), hour: +parts.hour };
}
function dayLabel(t, withMonth) {
  const d = new Date(t), n = d.getUTCDate();
  return `le ${JOURS[d.getUTCDay()]} ${n === 1 ? '1er' : n}${withMonth ? ' ' + MOIS[d.getUTCMonth()] : ''}`;
}
function etaHTML(min = 3, max = 7, cutoff = 14) {
  let { t, hour } = parisNow();
  if (!isWorkday(t) || hour >= cutoff) t = addWorkdays(t, 1);
  const a = addWorkdays(t, min), b = addWorkdays(t, max);
  const sameMonth = new Date(a).getUTCMonth() === new Date(b).getUTCMonth();
  return `Chez vous entre <strong>${dayLabel(a, !sameMonth)}</strong> et <strong>${dayLabel(b, true)}</strong>`;
}

/* ---------- Tiroir ---------- */
class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onUpdate = this.onUpdate.bind(this);
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
  }

  get panel() {
    return this.querySelector('[data-vl-panel]');
  }

  connectedCallback() {
    this.addEventListener('click', this.onClick);
    document.addEventListener('keydown', this.onKey);
    document.addEventListener(ThemeEvents.cartUpdated, this.onUpdate);
    document.addEventListener(ThemeEvents.cartOpen, this.open);
    document.addEventListener(ThemeEvents.cartClose, this.close);
    this.hydrate();

    if (window.Shopify?.designMode && window.theme?.isCartDrawerOpen) {
      this.open();
      window.theme.isCartDrawerOpen = false;
    }
    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:section:unload', (e) => {
        if (e.detail.sectionId !== this.dataset.sectionId) return;
        window.theme = window.theme || {};
        window.theme.isCartDrawerOpen = this.isOpen();
      });
      document.addEventListener('shopify:section:select', (e) => {
        if (e.detail.sectionId === this.dataset.sectionId) this.open();
      });
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    document.removeEventListener('keydown', this.onKey);
    document.removeEventListener(ThemeEvents.cartUpdated, this.onUpdate);
    document.removeEventListener(ThemeEvents.cartOpen, this.open);
    document.removeEventListener(ThemeEvents.cartClose, this.close);
    document.documentElement.classList.remove('vl-cart-lock');
  }

  hydrate() {
    const eta = this.querySelector('[data-vl-eta]');
    if (eta) {
      try { eta.innerHTML = etaHTML(); } catch (e) { /* le texte par défaut reste affiché */ }
    }
  }

  onClick(event) {
    if (event.target.closest('[data-action="close"]')) {
      event.preventDefault();
      this.close();
      return;
    }
    const checkout = event.target.closest('[data-ref="checkout-button"]');
    if (checkout) checkout.classList.add('is-loading');
  }

  onKey(event) {
    if (!this.isOpen()) return;
    if (event.key === 'Escape') { this.close(); return; }
    if (event.key !== 'Tab') return;
    const focusables = [...this.panel.querySelectorAll('a[href],button:not([disabled]),input:not([disabled])')].filter((el) => el.offsetParent !== null && el.tabIndex !== -1);
    if (!focusables.length) return;
    const first = focusables[0], last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  }

  isOpen() {
    return this.classList.contains('is-open');
  }

  open() {
    if (this.isOpen()) return;
    this.lastFocus = document.activeElement;
    this.hydrate();
    this.classList.add('is-open');
    this.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('vl-cart-lock');
    setTimeout(() => this.panel?.focus({ preventScroll: true }), 60);
  }

  close() {
    if (!this.isOpen()) return;
    this.classList.remove('is-open');
    this.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('vl-cart-lock');
    this.panel.querySelector('[data-ref="checkout-button"]')?.classList.remove('is-loading');
    if (this.lastFocus && document.contains(this.lastFocus)) this.lastFocus.focus({ preventScroll: true });
  }

  onUpdate(event) {
    this.renderCartContents(event.detail?.data || {});
  }

  renderCartContents(state) {
    const html = state.sections?.[this.dataset.sectionId];
    if (html) this.apply(html);
    else this.refetch();
  }

  async refetch() {
    try {
      const res = await fetch(`${window.location.pathname}?section_id=${encodeURIComponent(this.dataset.sectionId)}`);
      this.apply(await res.text());
    } catch (e) {
      console.error(e);
    }
  }

  apply(html) {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = doc.querySelector('[data-vl-panel]');
    if (!next) return;

    const scroller = this.panel.querySelector('[data-vl-scroll]');
    const scrollTop = scroller ? scroller.scrollTop : 0;
    const active = document.activeElement;
    const focusKey = active?.closest?.('[data-key]')?.dataset.key;
    const focusSel = active?.dataset?.qty ? `[data-qty="${active.dataset.qty}"]` : null;

    this.panel.innerHTML = next.innerHTML;
    this.panel.dataset.count = next.dataset.count;
    this.classList.toggle('cart-drawer--empty', next.dataset.count === '0');
    this.hydrate();

    const newScroller = this.panel.querySelector('[data-vl-scroll]');
    if (newScroller) newScroller.scrollTop = scrollTop;
    if (focusKey && focusSel) {
      this.panel.querySelector(`[data-key="${CSS.escape(focusKey)}"] ${focusSel}`)?.focus({ preventScroll: true });
    } else if (this.isOpen() && !this.panel.contains(document.activeElement)) {
      this.panel?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

/* ---------- Ligne d'article ---------- */
class CartDrawerItem extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.input = this.querySelector('[data-ref="quantity-selector-input"]');
    this.addEventListener('click', this.onClick);
    this.input?.addEventListener('change', this.onChange);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    this.removeEventListener('click', this.onClick);
    this.input?.removeEventListener('change', this.onChange);
  }

  onClick(event) {
    const step = event.target.closest('[data-qty]');
    if (step) {
      event.preventDefault();
      const value = Math.max(1, (parseInt(this.input.value, 10) || 1) + parseInt(step.dataset.qty, 10));
      this.input.value = value;
      this.querySelector('[data-qty="-1"]').disabled = value <= 1;
      clearTimeout(this.timer);
      this.timer = setTimeout(() => this.update(value), 350);
      return;
    }
    if (event.target.closest('[data-ref="remove-item"]')) {
      event.preventDefault();
      this.update(0);
    }
  }

  onChange() {
    let value = parseInt(this.input.value, 10);
    if (isNaN(value) || value < 0) value = 1;
    clearTimeout(this.timer);
    this.update(value);
  }

  async update(quantity) {
    const drawer = this.closest('cart-drawer');
    this.classList.add('is-loading');
    try {
      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: this.dataset.key,
          quantity,
          sections: drawer.dataset.sectionId,
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      if (!res.ok || data.status) {
        const message = data.description || data.message || 'Impossible de mettre à jour le panier.';
        document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message } }));
        drawer.refetch();
        return;
      }
      document.dispatchEvent(
        new CartUpdatedEvent({}, { itemCount: data.item_count, source: 'vl-cart-drawer', sections: data.sections }),
      );
    } catch (e) {
      console.error(e);
      this.classList.remove('is-loading');
    }
  }
}

if (!customElements.get('cart-drawer-item')) customElements.define('cart-drawer-item', CartDrawerItem);
